import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Icon,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Tabs,
} from "../components/ui";
import { AnnouncementPlayer } from "../components/AnnouncementPlayer";
import type { Tone } from "../lib/model";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.ts";
import type {
  CallDetail as CallDetailDto,
  CallListItem,
  CallsQuery,
  Disposition,
  EscalationDetail,
  EscalationSummary,
  InboxItem,
  InboxQuery,
  NmsConnectionStatus,
  OutageSummary,
  ProposalDecision,
  ProposalItem,
  TonightResponse,
  TranscriptTurn,
} from "../../lib/api.ts";
import {
  itemsOf,
  qk,
  useDemoMode,
  useMutate,
  usePermissions,
  useResource,
  useTenantZone,
} from "../../lib/useApi.ts";
import { DISPOSITION_LABELS } from "../../lib/format.ts";
import { clock, nightStartIso, secs, stamp } from "../lib/time.ts";

const DISPOSITIONS = Object.keys(DISPOSITION_LABELS) as Disposition[];

const dispositionTone = (d: Disposition | null): Tone =>
  d === "escalated"
    ? "amber"
    : d === "informed" || d === "ticket_filed"
      ? "green"
      : d === "message_taken"
        ? "blue"
        : "neutral";
const outcomeTone = (o: string): Tone =>
  o === "Escalated"
    ? "amber"
    : o === "Informed"
      ? "green"
      : o === "Message taken"
        ? "blue"
        : "neutral";
export function OperationsPage({ page }: { page: string }) {
  if (page === "calls") return <Calls />;
  if (page === "call") return <CallDetail />;
  if (page === "escalations" || page === "escalation")
    return <Escalations detail={page === "escalation"} />;
  if (page === "proposals") return <Proposals />;
  if (page === "inbox") return <Inbox />;
  return <Outage />;
}
function Calls() {
  const [params] = useSearchParams();
  const tz = useTenantZone();
  const initialOutcome = params.get("outcome");
  const [query, setQuery] = useState("");
  const [disposition, setDisposition] = useState<"" | Disposition>(
    DISPOSITIONS.includes(initialOutcome as Disposition) ? (initialOutcome as Disposition) : "",
  );
  const [win, setWin] = useState<"tonight" | "all">("tonight");

  const filters = { q: query.trim(), disposition, win };
  const list = useInfiniteQuery({
    queryKey: qk.calls(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const q: CallsQuery = { limit: 50 };
      if (filters.q) q.q = filters.q;
      if (filters.disposition) q.disposition = filters.disposition;
      if (filters.win === "tonight") q.from = nightStartIso(Date.now(), tz, 0);
      if (pageParam) q.cursor = pageParam;
      return api.calls(q);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  });

  const rows: CallListItem[] = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Night operations"
        title="Calls"
        description="Every conversation, with the details that matter."
        actions={
          <Badge tone="green" dot>
            {win === "tonight" ? "Tonight" : "All calls"}
          </Badge>
        }
      />
      <Tabs
        value={win}
        onChange={(v) => setWin(v as "tonight" | "all")}
        items={[
          { value: "tonight", label: "Tonight" },
          { value: "all", label: "All calls" },
        ]}
      />
      <div className="toolbar">
        <div className="search-field">
          <Icon name="search" />
          <input
            aria-label="Search calls"
            placeholder="Search caller, number, or reason"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className="input"
          aria-label="Filter by outcome"
          value={disposition}
          onChange={(e) => setDisposition(e.target.value as "" | Disposition)}
        >
          <option value="">Any outcome</option>
          {DISPOSITIONS.map((d) => (
            <option key={d} value={d}>
              {DISPOSITION_LABELS[d]}
            </option>
          ))}
        </select>
      </div>
      <Panel>
        {list.isLoading ? (
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading calls…</p>
          </div>
        ) : list.isError ? (
          <EmptyState
            icon="phone"
            title="Could not load the call log"
            text={(list.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" loading={list.isFetching} onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length ? (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Caller</th>
                    <th>Reason</th>
                    <th>Outcome</th>
                    <th>Length</th>
                    <th>
                      <span className="sr-only">Open call</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id}>
                      <td>{clock(c.startedAt, tz)}</td>
                      <td>
                        <Link to={`/calls/${c.id}`}>
                          <strong>{c.accountName || c.fromE164}</strong>
                        </Link>
                        <small>
                          {c.accountName ? c.fromE164 : "Caller unknown"}
                          {c.accountRef ? ` · ${c.accountRef}` : ""}
                        </small>
                      </td>
                      <td>
                        {c.intent || "—"}
                        {!c.endedAt && (
                          <small>
                            <Icon name="radio" size={12} /> Live now
                          </small>
                        )}
                        {c.proposalPending && (
                          <small>
                            <Icon name="flag" size={12} /> Approval pending
                          </small>
                        )}
                      </td>
                      <td>
                        {c.disposition ? (
                          <Badge tone={dispositionTone(c.disposition)}>
                            {DISPOSITION_LABELS[c.disposition]}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">In progress</Badge>
                        )}
                      </td>
                      <td>{secs(c.durationSec)}</td>
                      <td>
                        <Link
                          to={`/calls/${c.id}`}
                          className="button button-ghost"
                          aria-label={`Open call from ${c.accountName || c.fromE164}`}
                        >
                          <Icon name="chevron" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-footer">
              <span>
                {rows.length} {rows.length === 1 ? "call" : "calls"} loaded
              </span>
              {list.hasNextPage && (
                <Button loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                  Load 50 more
                </Button>
              )}
            </div>
          </>
        ) : (
          <EmptyState
            icon="phone"
            title={
              query || disposition
                ? "No calls match"
                : win === "tonight"
                  ? "No calls tonight"
                  : "No calls yet"
            }
            text={
              query || disposition
                ? "Try a different caller or outcome."
                : "Every call NightDesk answers is logged here the moment it hangs up."
            }
            action={
              query || disposition ? (
                <Button
                  onClick={() => {
                    setQuery("");
                    setDisposition("");
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
      </Panel>
    </>
  );
}
// Words that read wrong once split out of an identifier.
const WORDS: Record<string, string> = { sms: "SMS", eta: "ETA", followup: "follow-up", id: "ID", url: "URL" };
/** snake_case and camelCase identifiers into a short sentence: "sms_followup" → "SMS follow-up". */
const humanize = (s: string): string => {
  const words = s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => WORDS[w.toLowerCase()] ?? w.toLowerCase());
  const out = words.join(" ");
  return out.charAt(0).toUpperCase() + out.slice(1);
};

const EFFECT: Record<
  Extract<TranscriptTurn, { kind: "tool" }>["effect"],
  { kicker: string; meaning: string; tone: Tone }
> = {
  read: { kicker: "Read-only lookup", meaning: "Nothing changed anywhere.", tone: "green" },
  gated: {
    kicker: "Policy-gated action",
    meaning: "This changes state — it ran under policy, or became a proposal.",
    tone: "amber",
  },
  logged: { kicker: "Logged locally", meaning: "Recorded for a human. Nothing left the building.", tone: "blue" },
};

function FlagDialog({
  open,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const valid = reason.trim().length >= 2 && reason.trim().length <= 60;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Flag this transcript"
      description="It goes to the morning review queue with your note attached. The recording and derived values are unchanged."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={busy}
            onClick={() => onSubmit(reason.trim(), note.trim())}
          >
            Flag for review
          </Button>
        </>
      }
    >
      <Field label="Reason" hint="2–60 characters">
        <input value={reason} maxLength={60} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <Field label="Note (optional)">
        <textarea rows={4} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

function CallDetail() {
  const { id = "" } = useParams();
  const tz = useTenantZone();
  const { can } = usePermissions();
  const mayFlag = can("calls.flag");
  const [flagOpen, setFlagOpen] = useState(false);
  const [broken, setBroken] = useState(false);

  const query = useResource<CallDetailDto>(qk.call(id), () => api.call(id), {
    refetchIntervalMs: 5000,
  });
  const call = query.data;
  const live = !!call && !call.endedAt;

  const flag = useMutate<CallDetailDto, { reason: string; note?: string }>({
    mutationFn: (vars) => api.flagCall(id, vars),
    success: () => ({ title: "Transcript flagged", body: "It is in the review queue with your note attached." }),
    invalidate: [qk.call(id), ["calls"]],
    touchesTonight: true,
    onSuccess: () => setFlagOpen(false),
  });

  if (query.isLoading && !call) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Opening the call…</p>
      </div>
    );
  }
  if (query.isError || !call) {
    const status = (query.error as ApiError | null)?.status;
    return (
      <EmptyState
        icon="phone"
        title={status === 404 ? "Call not found" : "Could not load this call"}
        text={
          status === 404
            ? "It may belong to another account, or the id is wrong."
            : (query.error as ApiError | null)?.detail || "The console could not reach NightDesk."
        }
        action={
          status === 404 ? (
            <Link to="/calls" className="button">
              All calls
            </Link>
          ) : (
            <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
              Try again
            </Button>
          )
        }
      />
    );
  }

  const o = call.outcome;

  return (
    <>
      <Link to="/calls" className="text-button">
        <Icon name="back" size={16} />
        All calls
      </Link>
      <PageHeader
        eyebrow={`${clock(call.startedAt, tz)}${live ? " · live now" : ""}`}
        title={call.accountName || call.fromE164}
        description={`${call.fromE164}${call.accountRef ? ` · ${call.accountRef}` : ""}`}
        actions={
          <>
            {call.disposition ? (
              <Badge tone={dispositionTone(call.disposition)}>{DISPOSITION_LABELS[call.disposition]}</Badge>
            ) : (
              <Badge tone="neutral">In progress</Badge>
            )}
            {mayFlag && (
              <Button icon="flag" onClick={() => setFlagOpen(true)}>
                Flag transcript
              </Button>
            )}
          </>
        }
      />
      <div className="split-detail">
        <Panel title="Conversation">
          <div className="panel-body">
            {!call.recordingUrl || broken ? (
              <Notice>
                {broken
                  ? "The recording will not play in this browser. The transcript below is the complete record of what was said."
                  : "No recording for this call — it was off for this brand, declined, or aged out of retention. The transcript below is the record."}
              </Notice>
            ) : (
              <audio controls src={call.recordingUrl} preload="none" onError={() => setBroken(true)} />
            )}
          </div>
          <div className="transcript">
            {call.transcript.length ? (
              call.transcript.map((turn, i) =>
                turn.kind === "speech" ? (
                  <div key={i} className={`transcript-turn ${turn.role === "caller" ? "is-caller" : ""}`}>
                    <span className="icon-well">
                      <Icon name={turn.role === "caller" ? "user" : "moon"} size={22} />
                    </span>
                    <div>
                      <small>{turn.role === "caller" ? call.accountName || "Caller" : "NightDesk"}</small>
                      <p>{turn.text}</p>
                    </div>
                  </div>
                ) : (
                  <div key={i} className="action-card" data-effect={turn.effect}>
                    <div className="action-head">
                      <Badge tone={EFFECT[turn.effect].tone}>{EFFECT[turn.effect].kicker}</Badge>
                      <span className="action-tool">{turn.tool}</span>
                      {!turn.ok && <Badge tone="red">failed</Badge>}
                    </div>
                    <p>{turn.summary}</p>
                    <small className="muted">{EFFECT[turn.effect].meaning}</small>
                    {turn.proposalId && (
                      <Link to={`/proposals?id=${turn.proposalId}`} className="text-button">
                        Review the proposal this created <Icon name="arrow" size={14} />
                      </Link>
                    )}
                  </div>
                ),
              )
            ) : (
              <EmptyState
                title="No conversation recorded"
                text="The caller disconnected before anything was captured."
              />
            )}
          </div>
        </Panel>
        <div className="stack">
          <Panel title="How the call ended">
            <div className="panel-body">
              <dl className="detail-list">
                <div>
                  <dt>Disposition</dt>
                  <dd>{call.disposition ? DISPOSITION_LABELS[call.disposition] : "In progress"}</dd>
                </div>
                <div>
                  <dt>Length</dt>
                  <dd>{secs(call.durationSec)}</dd>
                </div>
                <div>
                  <dt>Ticket</dt>
                  <dd>
                    {o.ticketRef ? (
                      o.ticketRef
                    ) : o.ticketId ? (
                      <Link to={`/inbox?item=${o.ticketId}`} className="text-button">
                        Open in inbox
                      </Link>
                    ) : (
                      <span className="muted">None filed</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Proposal</dt>
                  <dd>
                    {o.proposalId ? (
                      <>
                        <Badge tone={o.proposalStatus === "pending" ? "amber" : "neutral"}>
                          {humanize(o.proposalStatus ?? "unknown")}
                        </Badge>{" "}
                        <Link to={`/proposals?id=${o.proposalId}`} className="text-button">
                          Review it
                        </Link>
                      </>
                    ) : (
                      <span className="muted">None — everything was pre-authorised</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Escalation</dt>
                  <dd>
                    {o.escalationId ? (
                      <Link to={`/escalations/${o.escalationId}`} className="text-button">
                        Open the escalation
                      </Link>
                    ) : (
                      <span className="muted">On-call was not paged</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Restore text</dt>
                  <dd>
                    {o.restoreTextSubscribed ? (
                      <Badge tone="green">Subscribed</Badge>
                    ) : (
                      <span className="muted">Not subscribed</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Turn latency p95</dt>
                  <dd>
                    {o.latencyP95NoToolMs === null ? "—" : `${o.latencyP95NoToolMs} ms`}{" "}
                    <span className="muted">no-tool</span> ·{" "}
                    {o.latencyP95ToolMs === null ? "—" : `${o.latencyP95ToolMs} ms`}{" "}
                    <span className="muted">tool-bound</span>
                  </dd>
                </div>
              </dl>
            </div>
          </Panel>
        </div>
      </div>
      <FlagDialog
        open={flagOpen}
        busy={flag.isPending}
        onClose={() => setFlagOpen(false)}
        onSubmit={(reason, note) => flag.mutate(note ? { reason, note } : { reason })}
      />
    </>
  );
}
const escStateTone = (s: string): Tone =>
  s === "resolved" ? "neutral" : s === "acked" ? "green" : s === "notifying" ? "red" : "amber";
const escSeverityTone = (s: string): Tone =>
  s === "critical" ? "red" : s === "high" ? "amber" : "neutral";

function EscalationRow({
  e,
  detail,
  mayAck,
  mayResolve,
  onAck,
  ackBusy,
  onResolve,
  tz,
}: {
  e: EscalationSummary;
  detail: boolean;
  mayAck: boolean;
  mayResolve: boolean;
  onAck: (e: EscalationSummary) => void;
  ackBusy: boolean;
  onResolve: (e: EscalationSummary) => void;
  tz: string | undefined;
}) {
  const open = e.state !== "resolved";
  const steps = detail ? (e as EscalationDetail).steps : undefined;
  return (
    <article className="record-row" key={e.id}>
      <span className="icon-well">
        <Icon name="bell" />
      </span>
      <div className="record-body">
        <div className="record-heading">
          <Link to={`/escalations/${e.id}`}>
            <h3>{e.accountName || e.fromE164}</h3>
          </Link>
          <Badge tone={escStateTone(e.state)}>{humanize(e.state)}</Badge>
          <Badge tone={escSeverityTone(e.severity)}>{humanize(e.severity)}</Badge>
          {e.verified && <Badge tone="green">Verified</Badge>}
        </div>
        <p>{e.summary}</p>
        <div className="record-meta">
          <span>{stamp(e.startedAt, tz)}</span>
          {e.currentTarget && (
            <span>
              {e.state === "acked" ? "Acknowledged by" : e.ackedBy ? "Acked by" : "Paging"} {e.currentTarget}
            </span>
          )}
          {e.callId && <Link to={`/calls/${e.callId}`}>Open call</Link>}
        </div>
        {e.resolutionNote && <p className="muted">Resolution: {e.resolutionNote}</p>}
        {detail && steps && steps.length > 0 && (
          <div className="evidence-block">
            <h3>Escalation timeline</h3>
            <ol className="step-list">
              {steps.map((s) => (
                <li key={s.index}>
                  <span className="step-index">
                    {s.ackedAt ? <Icon name="check" size={15} /> : s.index + 1}
                  </span>
                  <div>
                    {humanize(s.channel)} → {s.target}
                    <small>
                      {s.sentAt ? `Sent ${stamp(s.sentAt, tz)}` : "Not sent"}
                      {s.ackedAt ? ` · acknowledged ${stamp(s.ackedAt, tz)}` : ""}
                      {` · ${humanize(s.state)}`}
                    </small>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      {open && (
        <div className="button-row">
          {!e.ackedAt && (
            <Button variant="primary" loading={ackBusy} disabled={!mayAck} onClick={() => onAck(e)}>
              Acknowledge
            </Button>
          )}
          <Button disabled={!mayResolve} onClick={() => onResolve(e)}>
            Resolve
          </Button>
        </div>
      )}
    </article>
  );
}

function Escalations({ detail }: { detail: boolean }) {
  const { id = "" } = useParams();
  const tz = useTenantZone();
  const { can } = usePermissions();
  const mayAck = can("escalation.ack");
  const mayResolve = can("escalation.resolve");
  const [filter, setFilter] = useState<"open" | "resolved" | "all">("open");
  const [resolving, setResolving] = useState<EscalationSummary | null>(null);
  const [note, setNote] = useState("");

  const listQ = useResource(
    qk.escalations({ state: filter }),
    () => api.escalations({ state: filter }),
    { enabled: !detail, staleTimeMs: 10_000 },
  );
  const detailQ = useResource<EscalationDetail>(qk.escalation(id), () => api.escalation(id), {
    enabled: detail,
    refetchIntervalMs: detail ? 5000 : false,
  });

  const ack = useMutate<EscalationSummary, EscalationSummary>({
    mutationFn: (e) => api.ackEscalation(e.id),
    success: "Escalation acknowledged",
    invalidate: [qk.escalations({ state: filter }), qk.escalation(id)],
    touchesTonight: true,
  });
  const resolve = useMutate<EscalationDetail, { id: string; note: string }>({
    mutationFn: ({ id: eid, note: n }) => api.resolveEscalation(eid, n ? { note: n } : {}),
    success: "Escalation resolved",
    invalidate: [qk.escalations({ state: filter }), qk.escalation(id)],
    touchesTonight: true,
    onSuccess: () => setResolving(null),
  });

  const active = detail ? detailQ : listQ;
  const items: EscalationSummary[] = detail
    ? detailQ.data
      ? [detailQ.data]
      : []
    : itemsOf(listQ.data);

  return (
    <>
      {detail && (
        <Link className="text-button" to="/escalations">
          <Icon name="back" />
          All escalations
        </Link>
      )}
      <PageHeader
        eyebrow="Night operations"
        title={detail ? "Escalation detail" : "Escalations"}
        description="See who was paged, who answered, and what still needs attention."
      />
      {!detail && (
        <Tabs
          value={filter}
          onChange={(v) => setFilter(v as "open" | "resolved" | "all")}
          items={[
            { value: "open", label: "Open" },
            { value: "resolved", label: "Resolved" },
            { value: "all", label: "All" },
          ]}
        />
      )}
      <Panel>
        {active.isLoading ? (
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading escalations…</p>
          </div>
        ) : active.isError ? (
          <EmptyState
            icon="shield"
            title={
              (active.error as ApiError | null)?.status === 404 ? "Escalation not found" : "Could not load escalations"
            }
            text={(active.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" loading={active.isFetching} onClick={() => void active.refetch()}>
                Try again
              </Button>
            }
          />
        ) : items.length ? (
          items.map((e) => (
            <EscalationRow
              key={e.id}
              e={e}
              detail={detail}
              mayAck={mayAck}
              mayResolve={mayResolve}
              onAck={(x) => ack.mutate(x)}
              ackBusy={ack.isPending && ack.variables?.id === e.id}
              onResolve={(x) => {
                setResolving(x);
                setNote("");
              }}
              tz={tz}
            />
          ))
        ) : (
          <EmptyState title={detail ? "Escalation not found" : "No escalations in this view"} icon="shield" />
        )}
      </Panel>
      <Modal
        open={Boolean(resolving)}
        onClose={() => setResolving(null)}
        title="Resolve escalation"
        description={resolving ? resolving.accountName || resolving.fromE164 : undefined}
        footer={
          <>
            <Button onClick={() => setResolving(null)}>Cancel</Button>
            <Button
              variant="primary"
              loading={resolve.isPending}
              disabled={!mayResolve || (note.trim().length > 0 && note.trim().length < 3)}
              onClick={() => {
                if (!resolving) return;
                resolve.mutate({ id: resolving.id, note: note.trim() });
              }}
            >
              Resolve
            </Button>
          </>
        }
      >
        <Field label="Resolution note" hint="Optional, but recommended — 3+ characters if given.">
          <textarea
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was done?"
            maxLength={1000}
          />
        </Field>
      </Modal>
    </>
  );
}
function ProposalDiff({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  if (!after) return null;
  const keys = Object.keys(after);
  if (!keys.length) return null;
  // The payload is the tool's arguments; it rendered as JSON (`["power cycle"]`, `true`) on the
  // one screen where a person is asked to authorise it.
  const show = (v: unknown): string => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.length ? v.map(show).join(", ") : "—";
    if (typeof v === "object")
      return Object.entries(v as Record<string, unknown>)
        .map(([k, x]) => `${humanize(k)}: ${show(x)}`)
        .join("; ");
    return String(v);
  };
  return (
    <dl className="detail-list">
      {keys.map((k) => (
        <div key={k}>
          <dt>{humanize(k)}</dt>
          <dd>
            {before && k in before && show(before[k]) !== show(after[k]) ? (
              <>
                <span className="muted">{show(before[k])}</span> → <strong>{show(after[k])}</strong>
              </>
            ) : (
              <strong>{show(after[k])}</strong>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Proposals() {
  const [params, setParams] = useSearchParams();
  const { can } = usePermissions();
  const { demo } = useDemoMode();
  const mayDecide = can("proposals.decide");
  const [tab, setTab] = useState<"pending" | "decided" | "all">("pending");
  const [selected, setSelected] = useState<string | null>(params.get("id"));
  const [confirm, setConfirm] = useState<null | "approve_always" | "reject_never">(null);

  const list = useResource(qk.proposals({ status: tab }), () => api.proposals({ status: tab }), {
    staleTimeMs: 10_000,
  });
  const items = itemsOf(list.data);
  const proposal = items.find((p) => p.id === selected) || items[0];

  const decide = useMutate<ProposalItem, { id: string; decision: ProposalDecision }>({
    mutationFn: ({ id, decision }) => api.decideProposal(id, { decision }),
    success: (_d, v) =>
      v.decision.startsWith("approve") ? "Proposal approved" : "Proposal rejected",
    invalidate: [qk.proposals({ status: tab }), qk.proposals({ status: "all" })],
    touchesTonight: true,
    onSuccess: () => setConfirm(null),
  });

  function run(decision: ProposalDecision) {
    if (!proposal || !mayDecide) return;
    decide.mutate({ id: proposal.id, decision });
  }

  return (
    <>
      <PageHeader
        eyebrow="Morning review"
        title="Proposals"
        description="Review the evidence. You decide what happens next."
      />
      <Notice>
        A pending action has NOT happened yet. Approving authorizes it; approving or rejecting
        “always/never” also writes the standing policy.{demo ? " Outbound is simulated in demo mode." : ""}
      </Notice>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as "pending" | "decided" | "all")}
        items={[
          { value: "pending", label: "Waiting on you" },
          { value: "decided", label: "Decided" },
          { value: "all", label: "All proposals" },
        ]}
      />
      {list.isLoading ? (
        <Panel>
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading proposals…</p>
          </div>
        </Panel>
      ) : list.isError ? (
        <Panel>
          <EmptyState
            icon="shield"
            title="Could not load proposals"
            text={(list.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" loading={list.isFetching} onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        </Panel>
      ) : proposal ? (
        <div className="proposal-layout">
          <Panel className="proposal-sidebar">
            {items.map((p) => (
              <button
                className={p.id === proposal.id ? "selected" : ""}
                key={p.id}
                onClick={() => {
                  setSelected(p.id);
                  const next = new URLSearchParams(params);
                  next.set("id", p.id);
                  setParams(next, { replace: true });
                }}
              >
                <strong>{humanize(p.kind)}</strong>
                <small>{p.callId ? "From a call" : "System"}</small>
                <small>{humanize(p.status)}</small>
              </button>
            ))}
          </Panel>
          <Panel
            title={humanize(proposal.kind)}
            action={
              <Badge tone={proposal.status === "pending" ? "amber" : proposal.status === "approved" ? "green" : "neutral"}>
                {humanize(proposal.status)}
              </Badge>
            }
          >
            <div className="panel-body">
              <div className="evidence-block">
                <span className="eyebrow">Why this was proposed</span>
                <p>{proposal.rationale}</p>
                {proposal.callId && (
                  <Link to={`/calls/${proposal.callId}`} className="text-button">
                    Read the supporting call <Icon name="arrow" size={16} />
                  </Link>
                )}
              </div>
              {(proposal.before || proposal.after) && (
                <>
                  <span className="eyebrow">If you approve</span>
                  <ProposalDiff before={proposal.before} after={proposal.after} />
                </>
              )}
              {proposal.expiresAt && (
                <p className="muted">
                  If left undecided, this proposal expires {stamp(proposal.expiresAt, undefined)}.
                </p>
              )}
            </div>
            {proposal.status === "pending" && (
              <div className="action-footer">
                <Button variant="ghost" disabled={!mayDecide || decide.isPending} onClick={() => setConfirm("reject_never")}>
                  Reject &amp; never allow
                </Button>
                <Button disabled={!mayDecide} loading={decide.isPending && decide.variables?.decision === "reject"} onClick={() => run("reject")}>
                  Reject
                </Button>
                <Button disabled={!mayDecide || decide.isPending} onClick={() => setConfirm("approve_always")}>
                  Approve &amp; always allow
                </Button>
                <Button
                  variant="primary"
                  disabled={!mayDecide}
                  loading={decide.isPending && decide.variables?.decision === "approve"}
                  onClick={() => run("approve")}
                >
                  Approve once
                </Button>
              </div>
            )}
          </Panel>
        </div>
      ) : (
        <Panel>
          <EmptyState title="Nothing is waiting on you" text="The approval queue is clear." icon="shield" />
        </Panel>
      )}
      <Modal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={confirm === "approve_always" ? "Always allow this kind of action?" : "Never allow this kind of action?"}
        description="This decides this proposal AND writes a standing policy the server applies to future matching requests."
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Cancel</Button>
            <Button
              variant={confirm === "reject_never" ? "danger" : "primary"}
              loading={decide.isPending}
              disabled={!mayDecide}
              onClick={() => confirm && run(confirm)}
            >
              Confirm — change standing policy
            </Button>
          </>
        }
      >
        <p>
          {proposal ? humanize(proposal.kind) : "This kind"}:{" "}
          {confirm === "approve_always"
            ? "future matching requests will be permitted by policy without asking."
            : "future matching requests will be blocked by policy without asking."}
        </p>
      </Modal>
    </>
  );
}
const INBOX_KINDS: { label: string; value: "" | InboxItem["kind"] }[] = [
  { label: "All", value: "" },
  { label: "Message", value: "message" },
  { label: "Ticket", value: "ticket" },
  { label: "Lead", value: "lead" },
];
const inboxIcon = (k: InboxItem["kind"]): string =>
  k === "lead" ? "user" : k === "ticket" ? "file" : "message";
const inboxKindLabel = (k: InboxItem["kind"]): string =>
  k === "lead" ? "Lead" : k === "ticket" ? "Ticket" : "Message";

function Inbox() {
  const tz = useTenantZone();
  const { can } = usePermissions();
  const mayResolve = can("inbox.resolve");
  const [status, setStatus] = useState<"open" | "resolved" | "all">("open");
  const [kind, setKind] = useState<"" | InboxItem["kind"]>("");
  const [query, setQuery] = useState("");

  const filters: InboxQuery = { status, ...(kind ? { kind } : {}) };
  const list = useResource(qk.inbox(filters), () => api.inbox(filters), { staleTimeMs: 15_000 });

  const resolve = useMutate<InboxItem, InboxItem>({
    mutationFn: (item) => api.resolveInboxItem(item.id, { kind: item.kind }),
    success: "Inbox item resolved",
    invalidate: [qk.inbox(filters)],
    touchesTonight: true,
  });

  const all = itemsOf(list.data);
  const items = query.trim()
    ? all.filter((i) =>
        `${i.subject} ${i.body} ${i.accountName ?? ""}`.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : all;

  return (
    <>
      <PageHeader
        eyebrow="The overnight work"
        title="Inbox"
        description="Messages, tickets, and new customers ready for the morning."
      />
      <Tabs
        value={status}
        onChange={(v) => setStatus(v as "open" | "resolved" | "all")}
        items={[
          { value: "open", label: "Open" },
          { value: "resolved", label: "Resolved" },
          { value: "all", label: "All" },
        ]}
      />
      <div className="toolbar">
        <div className="search-field">
          <Icon name="search" />
          <input
            aria-label="Search inbox"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the loaded inbox"
          />
        </div>
        <select
          className="input"
          aria-label="Inbox kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as "" | InboxItem["kind"])}
        >
          {INBOX_KINDS.map((k) => (
            <option key={k.value || "all"} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>
      <Panel>
        {list.isLoading ? (
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading the inbox…</p>
          </div>
        ) : list.isError ? (
          <EmptyState
            icon="inbox"
            title="Could not load the inbox"
            text={list.error?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" loading={list.isFetching} onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : items.length ? (
          items.map((i) => {
            const open = i.status === "open";
            const busy = resolve.isPending && resolve.variables?.id === i.id;
            return (
              <article className="record-row" key={i.id}>
                <span className="icon-well">
                  <Icon name={inboxIcon(i.kind)} />
                </span>
                <div className="record-body">
                  <div className="record-heading">
                    <h3>{i.subject}</h3>
                    <Badge tone={open ? "blue" : "neutral"}>{open ? inboxKindLabel(i.kind) : "Resolved"}</Badge>
                  </div>
                  <p>{i.body}</p>
                  <div className="record-meta">
                    {i.accountName && <span>{i.accountName}</span>}
                    {i.callbackNumber && <span>{i.callbackNumber}</span>}
                    <span>{stamp(i.createdAt, tz)}</span>
                    {i.ticketRef && <span>#{i.ticketRef}</span>}
                  </div>
                </div>
                <div className="record-actions">
                  {i.callId && (
                    <Link to={`/calls/${i.callId}`} className="button button-ghost">
                      Call
                    </Link>
                  )}
                  <Button
                    disabled={!mayResolve || !open || resolve.isPending}
                    loading={busy}
                    onClick={() => resolve.mutate(i)}
                  >
                    {open ? "Resolve" : "Resolved"}
                  </Button>
                </div>
              </article>
            );
          })
        ) : (
          <EmptyState
            title="Your inbox is clear"
            text={query ? "No loaded items match that search." : "There are no items in this view."}
          />
        )}
      </Panel>
    </>
  );
}
const outageStatusTone = (s: string): Tone =>
  s === "resolved" ? "green" : s === "restoring" ? "blue" : s === "suspected" ? "neutral" : "amber";

function Outage() {
  const { can } = usePermissions();
  const { demo } = useDemoMode();
  const mayWrite = can("outage.write");
  const [modal, setModal] = useState<"eta" | "wording" | "declare" | "resolve" | null>(null);
  const [eta, setEta] = useState("");
  const [wording, setWording] = useState("");
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState("");
  const [count, setCount] = useState(0);

  const list = useResource(qk.outages, () => api.outages(), { refetchIntervalMs: 30_000 });
  const tonight = useResource<TonightResponse>(qk.tonight, () => api.tonight(), { staleTimeMs: 15_000 });
  const nms = useResource(qk.nms, () => api.nmsConnections(), { staleTimeMs: 60_000 });

  const active = list.data?.active ?? [];
  const recent = list.data?.recent ?? [];
  const primary: OutageSummary | undefined = active[0];
  const stormArmed = !!tonight.data?.stormArmed;

  const inval = { invalidate: [qk.outages], touchesTonight: true };
  const wordingM = useMutate<OutageSummary, { id: string; bannerText: string }>({
    mutationFn: ({ id, bannerText }) => api.updateOutage(id, { bannerText }),
    success: "Announcement saved",
    ...inval,
    onSuccess: () => setModal(null),
  });
  const etaM = useMutate<OutageSummary, { id: string; etaText: string | null }>({
    mutationFn: ({ id, etaText }) => api.updateOutage(id, { etaText }),
    success: (_d, v) => (v.etaText ? "ETA saved" : "ETA cleared"),
    ...inval,
    onSuccess: () => setModal(null),
  });
  const restoringM = useMutate<OutageSummary, string>({
    mutationFn: (id) => api.updateOutage(id, { status: "restoring" }),
    success: "Outage marked restoring",
    ...inval,
  });
  const resolveM = useMutate<OutageSummary, string>({
    mutationFn: (id) => api.resolveOutage(id, {}),
    success: "Outage resolved",
    ...inval,
    onSuccess: () => setModal(null),
  });
  const declareM = useMutate<OutageSummary, void>({
    mutationFn: () =>
      api.declareOutage({
        label: label.trim(),
        scope: scope.trim(),
        bannerText: wording.trim(),
        affectedCount: count,
      }),
    success: "Outage declared",
    ...inval,
    onSuccess: () => setModal(null),
  });
  const stormM = useMutate<{ stormArmed: boolean }, boolean>({
    mutationFn: (arm) => (arm ? api.armStorm({}) : api.disarmStorm()),
    success: (_d, arm) => (arm ? "Storm response armed" : "Storm response turned off"),
    ...inval,
  });

  return (
    <>
      <PageHeader
        eyebrow="Network"
        title="Outage"
        description="What affected callers hear, and what your team knows."
        actions={
          <Button
            icon="plus"
            variant="primary"
            disabled={!mayWrite}
            onClick={() => {
              setLabel("");
              setScope("");
              setWording("");
              setCount(0);
              setModal("declare");
            }}
          >
            Declare outage
          </Button>
        }
      />

      {list.isLoading ? (
        <Panel>
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading the outage board…</p>
          </div>
        </Panel>
      ) : list.isError ? (
        <Panel>
          <EmptyState
            icon="network"
            title="Could not load outages"
            text={(list.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" loading={list.isFetching} onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        </Panel>
      ) : (
        <>
          {primary?.announcementContradictsEta && (
            <Notice tone="amber">
              An ETA is set, but the announcement says there is no estimate. Update the wording before callers
              hear conflicting information — NightDesk never rewrites it for you.
            </Notice>
          )}
          <div className="split-detail">
            {primary ? (
              <Panel
                title={
                  <span className="outage-title">
                    {primary.label}
                    <Badge tone={outageStatusTone(primary.status)}>{humanize(primary.status)}</Badge>
                  </span>
                }
                action={<span className="muted">{primary.scope}</span>}
              >
                <div className="panel-body">
                  <div className="metric-line">
                    <span>
                      <strong>{primary.affectedCount}</strong> accounts affected
                    </span>
                    {primary.confirmedBy && (
                      <span>
                        Declared by <strong>{primary.confirmedBy}</strong>
                      </span>
                    )}
                    <span>
                      <strong>{primary.waitingOnRestoreText}</strong> restore-text requests
                    </span>
                  </div>
                  <hr />
                  <span className="eyebrow">Announcement for affected callers</span>
                  <blockquote className="quote">{primary.bannerText}</blockquote>
                  <div className="button-row" style={{ marginTop: 20 }}>
                    <AnnouncementPlayer text={primary.bannerText} compact />
                    <Button
                      icon="sliders"
                      disabled={!mayWrite}
                      onClick={() => {
                        setWording(primary.bannerText);
                        setModal("wording");
                      }}
                    >
                      Edit wording
                    </Button>
                  </div>
                  <hr />
                  <div className="section-line">
                    <div>
                      <span className="eyebrow">Restoration estimate</span>
                      <h3>{primary.etaText || "No estimate provided"}</h3>
                      <p className="muted">
                        {primary.etaText
                          ? primary.etaSource
                            ? `Entered by ${primary.etaSource}`
                            : "Operator-entered"
                          : "Only an operator can enter an ETA — the assistant never guesses one."}
                      </p>
                    </div>
                    <Button
                      disabled={!mayWrite}
                      onClick={() => {
                        setEta(primary.etaText || "");
                        setModal("eta");
                      }}
                    >
                      {primary.etaText ? "Edit ETA" : "Add ETA"}
                    </Button>
                  </div>
                </div>
                <div className="action-footer">
                  <Button
                    disabled={!mayWrite || primary.status === "restoring"}
                    loading={restoringM.isPending}
                    onClick={() => restoringM.mutate(primary.id)}
                  >
                    Mark restoring
                  </Button>
                  <Button variant="primary" disabled={!mayWrite} onClick={() => setModal("resolve")}>
                    Resolve outage
                  </Button>
                </div>
              </Panel>
            ) : (
              <Panel title="No active outage">
                <div className="panel-body">
                  <EmptyState
                    icon="check"
                    title="Nothing is down"
                    text="When an operator declares an outage, its announcement and controls appear here."
                  />
                </div>
              </Panel>
            )}
            <div className="stack">
              <Panel title="Storm response">
                <div className="panel-body">
                  <Badge tone={stormArmed ? "green" : "neutral"}>{stormArmed ? "Armed" : "Off"}</Badge>
                  <p className="muted" style={{ margin: "15px 0" }}>
                    Use shorter responses when a widespread outage drives call volume.
                  </p>
                  <Button
                    variant={stormArmed ? "default" : "primary"}
                    disabled={!mayWrite || stormM.isPending}
                    loading={stormM.isPending}
                    onClick={() => stormM.mutate(!stormArmed)}
                  >
                    {stormArmed ? "Turn off" : "Activate storm response"}
                  </Button>
                </div>
              </Panel>
              <Panel title="Monitoring">
                <div className="panel-body">
                  {nms.isLoading ? (
                    <p className="muted">Checking connections…</p>
                  ) : itemsOf(nms.data).length ? (
                    itemsOf(nms.data).map((c: NmsConnectionStatus) => (
                      <div key={c.id} className="section-line" style={{ marginBottom: 10 }}>
                        <div>
                          <strong>{c.label}</strong>
                          <p className="muted" style={{ margin: 0 }}>
                            {c.lastError
                              ? c.lastError
                              : c.lastEventAt
                                ? `Last event ${stamp(c.lastEventAt, undefined)}`
                                : "No recent event"}
                          </p>
                        </div>
                        <Badge tone={c.health === "ok" ? "green" : c.active ? "amber" : "neutral"}>
                          {humanize(c.health)}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <>
                      <Badge tone="amber">Not reporting</Badge>
                      <p className="muted" style={{ margin: "15px 0" }}>
                        No monitoring connection is reporting. The operator-entered announcement remains the
                        source for this outage.
                      </p>
                      <Link className="text-button" to="/integrations">
                        Review connections <Icon name="arrow" size={16} />
                      </Link>
                    </>
                  )}
                </div>
              </Panel>
            </div>
          </div>

          {(active.length > 1 || recent.length > 0) && (
            <Panel title="Other incidents" className="margin-top">
              {[...active.slice(1), ...recent].map((o) => (
                <article className="record-row" key={o.id}>
                  <span className="icon-well">
                    <Icon name="network" />
                  </span>
                  <div className="record-body">
                    <div className="record-heading">
                      <h3>{o.label}</h3>
                      <Badge tone={outageStatusTone(o.status)}>{humanize(o.status)}</Badge>
                    </div>
                    <p>{o.bannerText}</p>
                    <small className="muted">
                      {o.scope} · {o.affectedCount} accounts · ETA {o.etaText || "not set"}
                    </small>
                  </div>
                  {o.status !== "resolved" && (
                    <Button
                      disabled={!mayWrite || (resolveM.isPending && resolveM.variables === o.id)}
                      loading={resolveM.isPending && resolveM.variables === o.id}
                      onClick={() => resolveM.mutate(o.id)}
                    >
                      Resolve
                    </Button>
                  )}
                </article>
              ))}
            </Panel>
          )}
        </>
      )}

      <Modal
        open={modal === "eta"}
        onClose={() => setModal(null)}
        title="Restoration estimate"
        description="Use the wording an operator provided. The assistant cannot supply an estimate."
        footer={
          <>
            <Button onClick={() => setModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!mayWrite || !primary}
              loading={etaM.isPending}
              onClick={() => primary && etaM.mutate({ id: primary.id, etaText: eta.trim() || null })}
            >
              Save ETA
            </Button>
          </>
        }
      >
        {/* The example used to read "Around 6 AM", which the server rejects: it takes a
            duration or an exact date and time (outages.ts parseEtaInput). */}
        <Field
          label="Operator-provided estimate"
          hint="How long from now (3h, 90 minutes) or an exact time (2026-10-07 06:00). Leave blank to clear it."
        >
          <input value={eta} onChange={(e) => setEta(e.target.value)} placeholder="e.g. 3h" maxLength={160} />
        </Field>
        <Notice>Saving an ETA does not rewrite the announcement. Check the wording for consistency.</Notice>
      </Modal>

      <Modal
        open={modal === "wording"}
        onClose={() => setModal(null)}
        title="Outage announcement"
        footer={
          <>
            <Button onClick={() => setModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!mayWrite || !primary || !wording.trim()}
              loading={wordingM.isPending}
              onClick={() => primary && wordingM.mutate({ id: primary.id, bannerText: wording.trim() })}
            >
              Save wording
            </Button>
          </>
        }
      >
        <Field label="Exact wording">
          <textarea rows={6} value={wording} onChange={(e) => setWording(e.target.value)} maxLength={2000} />
        </Field>
      </Modal>

      <Modal
        open={modal === "declare"}
        onClose={() => setModal(null)}
        title="Declare an outage"
        description="Only state what your team knows."
        footer={
          <>
            <Button onClick={() => setModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!mayWrite || !label.trim() || !scope.trim() || !wording.trim()}
              loading={declareM.isPending}
              onClick={() => declareM.mutate()}
            >
              Declare outage
            </Button>
          </>
        }
      >
        <Field label="Incident name">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Mill Creek fiber cut" />
        </Field>
        <div className="grid-two">
          <Field label="Affected area or node">
            <input value={scope} onChange={(e) => setScope(e.target.value)} />
          </Field>
          <Field label="Accounts affected">
            <input
              type="number"
              min="0"
              value={count}
              onChange={(e) => setCount(Math.max(0, Number(e.target.value) || 0))}
            />
          </Field>
        </div>
        <Field label="What affected callers hear">
          <textarea rows={4} value={wording} onChange={(e) => setWording(e.target.value)} maxLength={2000} />
        </Field>
      </Modal>

      <Modal
        open={modal === "resolve"}
        onClose={() => setModal(null)}
        title={primary ? `Resolve ${primary.label}?` : "Resolve outage?"}
        description="Confirm service has been restored before clearing the outage."
        footer={
          <>
            <Button onClick={() => setModal(null)}>Keep open</Button>
            <Button
              variant="primary"
              disabled={!mayWrite || !primary}
              loading={resolveM.isPending}
              onClick={() => primary && resolveM.mutate(primary.id)}
            >
              Service is restored
            </Button>
          </>
        }
      >
        <p>
          {primary?.waitingOnRestoreText ?? 0} subscribers requested a restoration text. Sending remains subject to
          your policies.{demo ? " Outbound delivery is simulated in demo mode." : ""}
        </p>
      </Modal>
    </>
  );
}
